import { useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';

export const useJournalBookmark = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // Save bookmark to backend
    const saveBookmark = useCallback(async (path) => {
        if (path.startsWith('/journal/') && path !== '/journal') {
            try {
                // Extract section name from path (e.g., '/journal/checkin-04' -> 'checkin-04')
                const section = path.replace('/journal/', '');

                await api.post('/users/me/last-visited', {
                    section: 'journal',
                    path: path
                });
            } catch (error) {
                console.error('Failed to save bookmark:', error);
                // Silently fail - don't disrupt user experience
            }
        }
    }, []);

    // Get bookmark from backend
    const getBookmark = useCallback(async () => {
        try {
            const response = await api.get('/users/me');
            return response.data.user?.lastVisited?.path;
        } catch (error) {
            console.error('Failed to get bookmark:', error);
            return null;
        }
    }, []);

    // Restore to last page
    const restoreBookmark = useCallback(async () => {
        try {
            const lastPage = await getBookmark();
            if (lastPage && lastPage !== location.pathname && lastPage.startsWith('/journal/')) {
                navigate(lastPage, { replace: true });
                return true;
            }
        } catch (error) {
            console.error('Failed to restore bookmark:', error);
        }
        return false;
    }, [location.pathname, navigate, getBookmark]);

    // Auto-save on location change (debounced)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            saveBookmark(location.pathname);
        }, 500); // Debounce to avoid too many API calls

        return () => clearTimeout(timeoutId);
    }, [location.pathname, saveBookmark]);

    return {
        saveBookmark,
        getBookmark,
        restoreBookmark
    };
};
